import dayjs from "dayjs";
import type {Range} from "../utils/ts-utils";

export type DOI = Range<1, 6>;
export const DOIs: DOI[] = [1, 2, 3, 4, 5];

export const DoiStatus = [
    'Idea',
    'Quote requested / Validated',
    'Quoted / Prototyping',
    'Contract signed / Implemented',
    'EBIT effective',
]

export const Priority = [
    'Lowest',
    'Low',
    'Medium',
    'High',
    'Highest',
]

export const KpiFrequency = [
    'Daily',
    'Weekly',
    'Monthly',
    'Quarterly',
    'Yearly',
]

// NExO classifications. Values mirror the backend IntegerChoices; labels are
// resolved via i18n (sit namespace) keyed by the integer value.
export const ComplexityTierValues = [1, 2, 3] as const; // Farol: Complex / Strategic / Quick win
export type ComplexityTier = typeof ComplexityTierValues[number];

export const WorkstreamValues = [1, 2, 3] as const; // Frente: Procurement / Engineering / Market intelligence
export type Workstream = typeof WorkstreamValues[number];

export const ExecutionStatusValues = [1, 2, 3, 4, 5, 6, 7] as const;
export type ExecutionStatus = typeof ExecutionStatusValues[number];

// InitiativeCancellationReason: management / market / supplier / customer / other
export const CancellationReasonValues = [1, 2, 3, 4, 5] as const;
export type CancellationReason = typeof CancellationReasonValues[number];

export const BaselineSourceValues = [1, 2] as const; // 1 = Business plan, 2 = Adjusted prior year
export type BaselineSource = typeof BaselineSourceValues[number];

export enum SitRole {
    LOCAL = 'local',
    FINANCE = 'finance',
    GLOBAL = 'global',
    ADMIN = 'admin',
}

// Mirrors sit_audit.enums.AuditAction (backend).
export enum AuditAction {
    CREATE = 1,
    UPDATE = 2,
    DELETE = 3,
    VIEW = 4,
    STAGE_CHANGE = 5,
    CUSTOM = 99,
}

export const AuditActionLabel: Record<AuditAction, string> = {
    [AuditAction.CREATE]: 'Create',
    [AuditAction.UPDATE]: 'Update',
    [AuditAction.DELETE]: 'Delete',
    [AuditAction.VIEW]: 'View',
    [AuditAction.STAGE_CHANGE]: 'Stage change',
    [AuditAction.CUSTOM]: 'Custom',
}

// Mirrors sit_audit.enums.SitAuditModel (backend). Hand-assigned, stable across
// environments — append new values here to match new entries added there.
export enum SitAuditModel {
    INITIATIVE = 1,
    INITIATIVE_DESCRIPTIONS = 2,
    INITIATIVE_STATUS = 3,
    INITIATIVE_FINANCIAL = 4,
    COMPANY_TARGET = 5,
    SIT_CONFIGURATION = 6,
}

export const SitAuditModelLabel: Record<SitAuditModel, string> = {
    [SitAuditModel.INITIATIVE]: 'Initiative',
    [SitAuditModel.INITIATIVE_DESCRIPTIONS]: 'Confidential description',
    [SitAuditModel.INITIATIVE_STATUS]: 'DOI stage',
    [SitAuditModel.INITIATIVE_FINANCIAL]: 'Financials',
    [SitAuditModel.COMPANY_TARGET]: 'Target',
    [SitAuditModel.SIT_CONFIGURATION]: 'Configuration',
}

// `changes` (actual before/after field values) is intentionally not part of this
// type — the list API never returns it (see sit_audit.serializers.SitAuditLogSerializer).
export type AuditLogEntry = {
    id: number;
    actor: SimpleSitUserProfile | null;
    action: AuditAction;
    target_model: SitAuditModel;
    object_id: number;
    object_repr: string;
    metadata: Record<string, unknown>;
    created_at: string;
}

// Only returned by the detail endpoint, and only to ADMIN users
// (see sit_audit.views.SitAuditLogVS.retrieve).
export type AuditLogDetail = AuditLogEntry & {
    changes: {
        before: Record<string, unknown> | null;
        after: Record<string, unknown> | null;
    } | null;
}

// We should be careful to have as little as possible dependency between user model in CAT & SIT,
//  until SIT auth is designed, implemented, reviewed, ...
export type SimpleSitUserProfile = {
    id: number
    email: string
    name: string
}

export type SitUserProfile = SimpleSitUserProfile & {
    first_name: string
    last_name: string
    default_company: Company
    companies: number[]
    role: SitRole
    created: string
}

export interface Country {
    id: number;
    name: string;
    code_a3: string;
}

export interface Currency {
    id: number;
    name: string;
    code: string;
}

export interface Cluster {
    id: number;
    title: string;
}

export interface Division {
    id: number;
    title: string;
    code: string;
}

export interface ConversionRate {
    id: number;
    source: Currency;
    target: Currency;
    created: Date;
    rate: number;
}

export interface Cluster {
    id: number;
    title: string;
}

export interface Company {
    id: number;
    title: string;
    code: string;
    fiscal_year_start_month: number;
    cluster: Cluster;
    currency: Currency;
    country: Country;
}

export interface LeverGroup {
    id: number;
    title: string;
}

export interface Lever {
    id: number;
    title: string;
    description: string;
    group: LeverGroup;
}

export interface Category {
    id: number;
    level: number;
    l1: string;
    l2: string;
    l3: string;
    l4: string;
}

export type CategoryLabel = Pick<Category, 'id'> & {label: string}

export interface KPIType {
    id: number;
    title: string;
    description: string;
}

export type NexoIndicators = {
    mapped: number | null;
    unlocked: number | null;
    captured: number | null;
    // Derived (was SitConfiguration): portfolio target = Σ Target.saving_amount;
    // prioritized spend = Σ initiative baseline_spend.
    mapped_target: number | null;
    prioritized_spend: number | null;
    avoidance: number | null;
    avg_efficiency_pct: number | null;
    pending: number;
    count: number;
    by_gate: {doi_status: number; total: number | null; count: number}[];
    by_farol: {complexity_tier: number | null; total: number | null; count: number}[];
    by_workstream: {workstream: number; total: number | null; count: number}[];
    by_year: {year: number; total: number | null; target: number | null}[];
}

export type SitConfiguration = {
    id: number;
    report_currency: Currency | null;
    fiscal_reset_month: number;
    fiscal_year: number;
    review_interval_weeks: number;
    single_company: boolean;
    single_cluster: boolean;
    single_division: boolean;
    single_category: boolean;
    // When true, financial amounts are entered/shown in thousands (÷/×1000) and
    // labelled with a "K" prefix. Backend always stores base currency units.
    values_in_thousands: boolean;
    // When true, Initiative/Target tables show the full category path instead
    // of just the L1 label.
    show_full_category: boolean;
}

export type SitConfigurationWriter = Partial<Omit<SitConfiguration, 'report_currency'>> & {
    report_currency_id?: number | null;
}

export type Target = {
    id: number;
    title: string;
    target_year: number;
    baseline_spend: number;
    saving_amount: number;
    avoidance_amount: number;
    fiscal_year_saving_amount: number;
    fiscal_year_avoidance_amount: number;
    start_date: Date;
    owner: SimpleSitUserProfile | null;
    approver: SimpleSitUserProfile | null;
    local: boolean;
    lever?: Lever;
    currency_id: number;
    companies: number[];
    categories: number[];
    divisions: number[];
}

export type TargetWriter = Omit<Target, 'owner' | 'approver'>
    & {
        owner_id: number;
        approver_id: number;
    }

export type InitiativeFinancial = {
    id: number;
    active: boolean;
    affected_months: number;
    baseline_spend: number;
    investment: number;
    severance: number;
    saving_amount: number;
    fiscal_year_saving_amount: number;
    normal_year_saving_amount: number;
    fiscal_carry_over_saving_amount: number;
    normal_carry_over_saving_amount: number;
    saving_percentage: number;
    carry_over_saving_percentage: number;
    avoidance_amount: number;
    fiscal_year_avoidance_amount: number;
    normal_year_avoidance_amount: number;
    fiscal_carry_over_avoidance_amount: number;
    normal_carry_over_avoidance_amount: number;
    avoidance_percentage: number;
    carry_over_avoidance_percentage: number;
    // NExO negotiation snapshot (deltas are computed at read time)
    baseline_source: BaselineSource | null;
    baseline_evolved: number;
    bp_value: number;
    estimated_efficiency: number;
    first_proposal: number;
    negotiated_value: number;
    volume: number;
    initiative: number;
    categories: number[];
    companies: number[];
    divisions: number[];
    currency_id: number;
    saving_method: number;
    parent?: number;
}

export type InitiativeStatus = {
    id: number;
    initiative: number;
    doi_stage: number;
    due_date: Date;
    actual_date: Date | null;
    presented: boolean;
    presented_date: Date | null;
    created: Date;
}

export type InitiativeEvidence = {
    id: number;
    initiative: number;
    url: string;
    label: string;
    created: Date;
}

export type InitiativeSuccessStep = {
    id: number;
    initiative: number;
    title: string;
    description: string;
    due_date: Date;
    achieved: boolean;
    created?: Date;
    updated?: Date;
}

export type InitiativeKPIValue = {
    id: number;
    initiative_kpi: number;
    value: number;
    created: Date;
    creator: SitUserProfile;
}

export type InitiativeKPI = {
    id: number;
    initiative: number;
    type_id: number;
    values: InitiativeKPIValue[];
    title: string;
    description: string;
    frequency: number;
    created: Date;
}

export type InitiativeDescriptions = {
    id: number;
    description: string;
    confidential_description: string;
    notes: string;
    initiative: number;
    approval_notes: string;
}

export type Initiative = {
    id: number;
    title: string;
    customer_id: string;
    locked_by: SimpleSitUserProfile | null;
    confidential: boolean;
    nda_required: boolean;
    sustainability: boolean;
    risk_reduction: boolean;
    compliance: boolean;
    start_date: Date;
    created: Date;
    updated: Date;
    owner: SimpleSitUserProfile | null;
    responsible: SimpleSitUserProfile | null;
    approver: SimpleSitUserProfile | null;
    approved: boolean;
    local: boolean;
    lever_id: number;
    description?: InitiativeDescriptions;
    financial: InitiativeFinancial[];
    doi: InitiativeStatus[];
    success_step: InitiativeSuccessStep[];
    kpi: InitiativeKPI[];
    evidence: InitiativeEvidence[];
    team: string[];
    skus: string[];
    doi_status: number;
    // How the saving split is entered: 'percentage' (children derived) or 'amount'
    // (each participant has independent financials; primary = Σ children).
    split_mode: 'percentage' | 'amount';
    priority: number;
    // NExO classifications (optional)
    complexity_tier: ComplexityTier | null;
    workstream: Workstream | null;
    execution_status: ExecutionStatus | null;
    contract_start: Date | null;
    contract_end: Date | null;
    cancellation_reason: number | null;
}

export type InitiativeWriter =
    Omit<Initiative, 'description' | 'financial' | 'doi' | 'success_step' | 'skus' | 'evidence' | 'owner' | 'responsible' | 'approver'>
    & {
        description_w: string;
        description_w_confidential: string;
        description_w_notes: string;
        approval_notes_w: string;
        financial_w: InitiativeFinancial[];
        doi_w: InitiativeStatus[];
        success_step_w: InitiativeSuccessStep[];
        skus_w: string[];
        evidence_w: InitiativeEvidence[];
        owner_id: number;
        responsible_id: number;
        approver_id: number;
    }

export type Attachment = {
    id: number;
    file: string;
    created: Date;
}

export type SaveErrorsResponse = {
    [key: string]: string[] | SaveErrorsResponse[];
}

// Cost Saving methods
export interface CSMStandard {
    old_price: number;
    new_price: number;
    quantity_per_month: number;
}

export interface CSMManufacturing {
    old_process_time: number;
    new_process_time: number;
    labor_rate: number;
    quantity_per_year: number;
}

export interface CSMOutsourcing {
    new_landed_price: number;
    marginal_costs: number;
    quantity_per_year: number;
}

export interface CSMDemandControl {
    spend_previous_period: number;
    spend_current_period: number;
}

export interface CSMCapex {
    first_offer_received: number;
    signed_offer: number;
}

export type FirstPageKpis = {
    count: number;
    total_savings: number;
    pending: number;
}

export function category_readable_str(category?: Category, join_str = ' -> '): string {
    if (!category) return '';
    let res = category.l1;
    if (category.l2.trim() !== '') {
        res += join_str + category.l2;
        if (category.l3.trim() !== '') {
            res += join_str + category.l3;
            if (category.l4.trim() !== '') {
                res += join_str + category.l4;
            }
        }
    }
    return res;
}

export function traffic_light(initiative: Initiative): string[] {
    if (initiative.doi_status === 5) return ["green", "Completed"]
    if (initiative.doi === undefined) return ["gray", ""];
    const next_due = initiative.doi.find(
        (doi) => doi.doi_stage === (initiative.doi_status + 1));
    if (!next_due) return ["gray", ""];
    const diff_day = dayjs(next_due.due_date).diff(dayjs(), 'days');
    if (diff_day >= 5) return ["green", "On track, " + diff_day.toString() + " days to next deadline"];
    if (diff_day < 5 && diff_day >= 0) return ["yellow", diff_day.toString() + " days to next deadline"];
    if (diff_day < 0) return ["red", (-1 * diff_day).toString() + " days passed deadline"];
    return ["gray", ""];
}

export type ValueType = 'rolling' | 'fiscal_year';
export type SavingValue =
    'rolling_saving'
    | 'fiscal_year_saving'
    | 'rolling_saving_adj'
    | 'fiscal_year_saving_adj'
export type AvoidanceValue =
    'rolling_avoidance'
    | 'fiscal_year_avoidance'
    | 'rolling_avoidance_adj'
    | 'fiscal_year_avoidance_adj'
export type DimensionValue = SavingValue | AvoidanceValue

export type TargetValues = 'saving'
    | 'avoidance'

type Values = {
    [key in DimensionValue]: number
}

type TValues = {
    [key in TargetValues]: number
}

export type ByApprovedRowResponse = {
    approved: number
} & Values
export type PerDoiRowResponse = {
    month: string
    doi: DOI
} & Values
export type PerCompanyLeverRowResponse = {
    id: number
    title: string
    is_doi5: boolean
    lever_group_title: string
} & Values
export type PerCompanyRowResponse = Pick<Company, 'id' | 'code' | 'title'> & Values
export type TValuePerCompanyRowResponse = Pick<Company, 'id' | 'code' | 'title'> & TValues
export type PerClusterRowResponse = Pick<Cluster, 'id' | 'title'> & {
    doi: DOI
    month: string
} & Values
export type PerClustercumSumRowResponse = Pick<Cluster, 'id' | 'title'> & {
    month: string
} & Values
export type TValuePerMonthRowResponse = {
    month: string
} & TValues
export type TValuePerClusterMonthRowResponse = Pick<Cluster, 'id' | 'title'> & {
    month: string
} & TValues

export function getTargetValue(activeValueType: ValueType): TargetValues {
    switch (activeValueType) {
        case 'rolling':
        case 'fiscal_year':
            return 'saving';
    }
}

export function getSavingsValue(activeValueType: ValueType, probabilityAdjusted: boolean): SavingValue {
    switch (activeValueType) {
        case 'rolling':
            return probabilityAdjusted ? 'rolling_saving_adj' : 'rolling_saving';
        case 'fiscal_year':
            return probabilityAdjusted ? 'fiscal_year_saving_adj' : 'fiscal_year_saving';
    }
}

export function getAvoidanceValue(activeValueType: ValueType, probabilityAdjusted: boolean): AvoidanceValue {
    switch (activeValueType) {
        case 'rolling':
            return probabilityAdjusted ? 'rolling_avoidance_adj' : 'rolling_avoidance';
        case 'fiscal_year':
            return probabilityAdjusted ? 'fiscal_year_avoidance_adj' : 'fiscal_year_avoidance';
    }
}
